// src/pages/students/details/ExamsTab.jsx
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import { useExamRegistrations } from '../../../hooks/useExamRegistrations';

export default function ExamsTab({ studentId }) {
  const navigate = useNavigate();

  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
    softDeleteExamRegistrationSession,
  } = useExamRegistrations(studentId);

  const [deletingId, setDeletingId] = useState(null);

  const badgeClass = (s) => {
    switch (s) {
      case 'Registered':
        return 'bg-green-100 text-green-800';
      case 'Deferred':
        return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '—';

  const currencyMY = (n) =>
    n == null || n === ''
      ? '—'
      : `RM${Number(n).toLocaleString('en-MY', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  const onAdd = () => {
    // student-scoped "new" flow
    navigate(`/students/${studentId}/exams/new`);
    // If you prefer reusing admin route, use:
    // navigate(`/exam-registrations/new?studentId=${studentId}&fromStudent=${studentId}`);
  };

  const handleRemove = async (row) => {
    const ok = window.confirm(
      'Remove this exam registration? This is a soft delete (parent + subjects) and can be restored from Admin.'
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await softDeleteExamRegistrationSession({ id: row.id, studentId });
    } catch (e) {
      console.error(e);
      alert('Failed to remove registration.');
    } finally {
      setDeletingId(null);
    }
  };

  const rows = useMemo(() => sessions, [sessions]);

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-gray-900">Exams</h2>
            {/* Optionally show a subtle indicator when reloading:
                {isFetching && rows.length > 0 && <span className="text-xs text-gray-400">Refreshing…</span>} */}
          </div>
          <Button onClick={onAdd}>Add Registration</Button>
        </div>

        {/* States */}
        {isLoading ? (
          <div className="py-8 text-center text-gray-500">Loading exams…</div>
        ) : isError ? (
          <div className="py-8 text-center text-red-600">Error: {String(error)}</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No exam registrations yet{' '}
            <button className="text-blue-600 hover:text-blue-800 underline" onClick={onAdd}>
              Add Registration
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subjects
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reg. Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pay Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Venue
                  </th>
                  {/* Replaced "Remarks" with "Passed" */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row) => {
                  const kids = row.infos || []; // child subjects (non-deleted expected from hook)
                  const total = kids.length;
                  const passed = kids.filter((k) => k.result_status === 'Passed').length;
                  const pending = kids.filter((k) => k.result_status === 'Pending').length;

                  return (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {/* Session */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {row.exam_sessions?.name || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(row.exam_sessions?.start_date)} — {formatDate(row.exam_sessions?.end_date)}
                        </div>
                      </td>

                      {/* Subjects */}
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap">
                          {kids.length === 0 ? (
                            <span className="text-sm text-gray-500">—</span>
                          ) : (
                            kids.map((k) => (
                              <span
                                key={k.id}
                                className="px-2 py-0.5 mr-1 mb-1 rounded-full bg-gray-100 text-gray-800 text-xs"
                                title={k.subjects?.name || ''}
                              >
                                {k.subjects?.code || '—'}
                              </span>
                            ))
                          )}
                        </div>
                      </td>

                      {/* Parent reg status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badgeClass(
                            row.registration_status
                          )}`}
                        >
                          {row.registration_status || '—'}
                        </span>
                      </td>

                      {/* Fee */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{currencyMY(row.total_fee_paid)}</div>
                      </td>

                      {/* Payment date */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(row.payment_date)}</div>
                      </td>

                      {/* Venue */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.venue || '—'}</div>
                      </td>

                      {/* Passed aggregation (replaces Remarks) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {passed}/{total}
                          {pending > 0 ? (
                            <span className="text-gray-500"> (pending:{pending})</span>
                          ) : null}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/exam-registrations/${row.id}?fromStudent=${studentId}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View
                          </Link>
                          <Link
                            to={`/exam-registrations/${row.id}/edit?fromStudent=${studentId}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleRemove(row)}
                            disabled={deletingId === row.id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            title="Remove registration"
                          >
                            {deletingId === row.id ? 'Removing…' : 'Remove'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
